import { useQuery } from '@tanstack/react-query';

import type {
  AdminPolicyEndorsementQueueFilters,
  ApiSuccess,
  MyPolicyEndorsementQueueFilters,
  PolicyEndorsementDetail,
  PolicyEndorsementQueueRow,
  QueueFilterOption,
} from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

import { createQueueListingHook } from './queue/createQueueListingHook';

type QueuePayload = { count: number; rows: PolicyEndorsementQueueRow[] };

export const useEndorsementQueue = createQueueListingHook<
  PolicyEndorsementQueueRow,
  AdminPolicyEndorsementQueueFilters
>({
  baseQueryKey: queryKeys.policyEndorsements.admin(),
  queryKey: (filters) =>
    queryKeys.policyEndorsements.adminList(filters as Record<string, unknown> | undefined),
  fetchPage: async (filters) => {
    const response = await apiClient.get<ApiSuccess<QueuePayload>>('/admin/policy-endorsements', {
      params: filters,
    });
    return response.data.data;
  },
  getPageParams: (filters) => ({ page: filters?.page, limit: filters?.limit }),
  staleTime: 15_000,
});

export const useMyEndorsementQueue = createQueueListingHook<
  PolicyEndorsementQueueRow,
  MyPolicyEndorsementQueueFilters
>({
  baseQueryKey: queryKeys.policyEndorsements.mine(),
  queryKey: (filters) =>
    queryKeys.policyEndorsements.mineList(filters as Record<string, unknown> | undefined),
  fetchPage: async (filters) => {
    const response = await apiClient.get<ApiSuccess<QueuePayload>>('/policy-endorsements/mine', {
      params: filters,
    });
    return response.data.data;
  },
  getPageParams: (filters) => ({ page: filters?.page, limit: filters?.limit }),
  staleTime: 15_000,
});

export function useEndorsementDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.policyEndorsements.detail(id ?? ''),
    queryFn: async (): Promise<PolicyEndorsementDetail> => {
      const response = await apiClient.get<ApiSuccess<PolicyEndorsementDetail>>(
        `/policy-endorsements/${id}`,
      );
      return response.data.data;
    },
    enabled: Boolean(id),
  });
}

export async function fetchEndorsementConsultantOptions(
  search: string,
): Promise<QueueFilterOption[]> {
  const response = await apiClient.get<ApiSuccess<QueuePayload>>('/admin/policy-endorsements', {
    params: { search: search || undefined, page: 1, limit: 100 },
  });
  const unique = new Map<string, string>();
  for (const row of response.data.data.rows) {
    if (row.consultantProfileId && row.consultantName) {
      unique.set(row.consultantProfileId, row.consultantName);
    }
  }
  return [...unique].map(([value, label]) => ({ value, label }));
}

export const usePolicyEndorsement = useEndorsementDetail;
